import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, Subject, Subscription, take } from 'rxjs';
import { Platform, ToastController } from '@ionic/angular';

import { CustomerModel } from 'src/app/models/customer-model';
import { DateFormatPipe } from 'src/app/pipes/date-format/date-format.pipe';
import type { ILoginRequestData, ILoginResponsePayload } from 'src/app/interfaces/authentication.interface';
import { LoginResponseComments } from 'src/app/interfaces/authentication.interface';
import { Navigator } from "src/app/services/routing/navigator";

import { AppsflyerService } from '../marketing/appsflyer.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { ConfigService } from '../config/config.service';
import { EventsService } from '../event/event.service';
import { isUndefined } from '../codeUtils';
import { eMfaStatuses, UserSessionService } from '../user-session/user-session.service';
import { UniqueIDService } from '../unique-id/unique-id.service';
import { ResponsibleGamingService } from '../responsible-gaming-service/responsible-gaming.service';
import { GeoResponse } from 'src/app/interfaces/geo-interface';
import { JurisdictionService } from '../geo/jurisdiction.service';
import { GeoLocationStrategyService } from '../geo-location-strategy/geo-location-strategy.service';
import { GeoLocationControllerService } from '../geo-location/geo-location-controller.service';

@Injectable({
  providedIn: 'root'
})

export class LoginService {

  _loginInfoSubject: BehaviorSubject<any>;
  public loginInfo: ILoginResponsePayload;
  public customerIPAddress: any;
  public customerLastLogoutReason: any;
  public reconfirmPersonalDetails: any;
  public customerID: string | number;
  public toastMessageQue: any = [];
  boydSubscription: Subscription
  private isFromBiometrics: boolean = false;
  isSMS_OR_AuthApp;
  customer: CustomerModel

  constructor(
    private navigator: Navigator,
    private userSessionService: UserSessionService,
    private eventsService: EventsService,
    private config: ConfigService,
    public toastController: ToastController,
    public appsFlyerService: AppsflyerService,
    public authenticationService: AuthenticationService,
    public uniqueIDService: UniqueIDService,
    private geoLocationControllerService: GeoLocationControllerService,
    private geoLocationStrategyService: GeoLocationStrategyService,
    public responsibleGamingProvider: ResponsibleGamingService,
    private jurisdiction: JurisdictionService,
    private dateFormatPipe: DateFormatPipe,
    private platform: Platform
  ) {
    this._loginInfoSubject = new BehaviorSubject(undefined);
  }

  public loginCustomer(data: ILoginRequestData) {
    // let trackingData = this.appsFlyerService.getTrackingIDs();
    // data.deviceDetails = trackingData.deviceDetails;
    // data.externalSource = trackingData.externalSource;

    let response = new Subject();
    this.authenticationService.callLogin(data).subscribe({
      next: resp => {
        if (!!resp.result && resp.payload) {
          // Adding Toast Message Queue
          if (!isUndefined(resp.payload.lastLogoutReason) && (resp.payload.lastLogoutReason == 'GAME_CLT_LOGOUT' || resp.payload.lastLogoutReason == 'APP_INACTIVITY')) {
            this.toastMessageQue.push('INACTIVITY');
          }
          this.setUserLogin(resp.payload);
          response.next(resp.payload);
          response.complete();
        }
      }, error: (error) => {
        response.error(error);
        response.complete();
      }
    });
    return response;
  }

  public setUserLogin(loginInfo: ILoginResponsePayload) {
    if (!!loginInfo) {
      localStorage.setItem('userSession.loginInfo', JSON.stringify(loginInfo));
      this.loginInfo = loginInfo;
      // this.loginInfo.hasNotUpdatedHouseRules = true;
    } else {
      localStorage.removeItem('userSession.loginInfo');
      this.loginInfo = loginInfo;
    }
    this.userSessionService.setUserStatus(this.loginInfo)
    this._loginInfoSubject.next(this.loginInfo);
  }

  public getUserLogin() {
    return this._loginInfoSubject;
  }

  async setLoginResponseInStorage(fromBiometric = false) {
    this.isFromBiometrics = fromBiometric;
    const loginInfo = this.loginInfo;
    if (loginInfo) {
      localStorage.setItem('clientApp', this.config.getClientApp());
      if ('lastIPAddress' in loginInfo) {
        this.setCustomerIPAddress(loginInfo.lastIPAddress);
      }
      if ('lastLogoutReason' in loginInfo) {
        this.setCustomerLastLogoutReason(loginInfo.lastLogoutReason);
      }

      if ('customerId' in loginInfo) {
        this.setCustomerID(loginInfo.customerId)
      }

      if ('reconfirmPersonalDetails' in loginInfo) {
        this.setCustomerReconfirmationPersonalInfo(loginInfo.reconfirmPersonalDetails);
      }

      /*else if ('hasNotUpdatedPrivacyPolicy' in this.loginInfo) {
        delete this.loginInfo.hasNotUpdatedPrivacyPolicy
        localStorage.setItem('hasNotUpdatedPrivacyPolicy', 'true');
        //this.navigator.navigateTo("UpdateTocPage", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.setLoginResponseInStorage() })
        //page is not added yet but, to validate putting simple testing code to confirm logic flow

        let alert = await this.alertController.create({
          header: 'hasNotUpdatedPrivacyPolicy',
          message: 'The Terms and Conditions have recently been updated. Please review them and agree here to continue to have access to the service.',
          buttons: [
            {
              text: 'AGREE',
              role: 'confirm',
              handler: () => {
                localStorage.removeItem('hasNotUpdatedPrivacyPolicy');
                this.setLoginResponseInStorage();
              },
            },
          ],
        });
        alert.present();
    } else {*/
      const needMFA =
        loginInfo?.comments?.indexOf(LoginResponseComments.CUSTOMER_MFA_REQUIRED) > -1
      if (needMFA) {
        if (loginInfo.deviceId) {
          this.uniqueIDService.setMFADeviceIdAsObservable(loginInfo.deviceId);
        }
        this.userSessionService.refreshCustomer().subscribe((customer: CustomerModel) => {
          if (customer) {
            this.customer = customer;
            this.startMFA();
          }
        })
      }
      else {
        this.userSessionService.refreshCustomer().subscribe((customer: CustomerModel) => {
          if (customer) {
            this.customer = customer;
            this.checkLoginActions();
          }
        })
      }
    }
  }

  public setCustomerIPAddress(IPAddress: any) {
    localStorage.setItem('lastIPAddress', IPAddress);
    this.customerIPAddress = IPAddress;
  }

  public getCustomerIPAddress() {
    return this.customerIPAddress;
  }

  public setCustomerLastLogoutReason(LogoutReason: any) {
    localStorage.setItem('lastLogoutReason', LogoutReason);
    this.customerLastLogoutReason = LogoutReason;
  }

  public getCustomerLastLogoutReason() {
    return this.customerLastLogoutReason;
  }

  public setCustomerReconfirmationPersonalInfo(reconfirmationInfo: any) {
    localStorage.setItem('reconfirmPersonalDetails', reconfirmationInfo);
    this.reconfirmPersonalDetails = reconfirmationInfo;
  }

  public getCustomerReconfirmationPersonalInfo() {
    return this.reconfirmPersonalDetails;
  }

  public clearCustomerReconfirmationPersonalInfo() {
    localStorage.removeItem('reconfirmPersonalDetails');
  }

  public setCustomerID(customerID: any) {
    localStorage.setItem('customerID', customerID);
    this.customerID = customerID;
    this.userSessionService.setCustomerId(customerID);
  }

  public getCustomerID() {
    return this.customerID;
  }

  public initToastMessages() {
    this.userSessionService.getCustomer().subscribe(
      (customer) => {
        if (customer.lastLoginTime) {
          this.toastMessageQue.push('LASTLOGIN');
        }
        if (!isUndefined(customer.sendEmailUponLogin)) {
          if (customer.sendEmailUponLogin == true) {
            this.toastMessageQue.push('OPTEDINEMAIL');
          }
        }
        this.runToastMessages();
      }).unsubscribe();
  }

  public runToastMessages() {
    if (this.toastMessageQue.length) {
      let e = this.toastMessageQue[0];
      this.toastMessageQue.shift();
      this.buildToast(e);
    }
  }

  async buildToast(e) {
    let toast_message = '';

    this.userSessionService.getCustomer().subscribe(
      (customer) => {
        switch (e) {
          case 'LASTLOGIN':
            toast_message = 'Last login time: ' + this.dateFormatPipe.transform(customer.lastLoginTime, this.config.getConfig().FEATURE_SITE_COUNTRY_REGION);
            break;
          case 'INACTIVITY':
          case 'APP_INACTIVITY':
            toast_message = 'Your last session expired due to inactivity';
            break;
          case 'OPTEDINEMAIL':
            toast_message = 'You are opted in to receiving an email upon every login';
            break;
        }
        this.presentToast(toast_message);
      }).unsubscribe();
  }

  async presentToast(toast_message) {
    let toastCssClass = "test";
    if(this.platform.is("capacitor")) {
      toastCssClass = "toast-safe-area-adjust";
    }
    const toast = await this.toastController.create({
      message: toast_message,
      position: 'top',
      duration: 10000,
      cssClass: toastCssClass,
      color: "secondary",
      buttons: [
        {
          text: 'Close',
          role: 'close',
          handler: () => { this.runToastMessages(); }
        }
      ]
    });

    await toast.present();
    const { role } = await toast.onDidDismiss();

  }

  private startMFA() {
    this.isSMS_OR_AuthApp;
    const kycStepValue = this.getKycStepValue(this.customer);

    if (kycStepValue) {
      this.isSMS_OR_AuthApp = kycStepValue == 'SMS' ? 'SMS' : 'AUTH_APP'
    } else {
      this.isSMS_OR_AuthApp = this.config.getConfig().FEATURE_MFA_TYPE ?? 'SMS';
    }

    if (this.loginInfo.comments) {
      if (this.loginInfo.comments.indexOf(LoginResponseComments.CUSTOMER_MFA_REQUIRED) > -1) {
        this.userSessionService.setLoginResponseForMFA();
      }
    }

    const mfa_statuses = this.config.getConfig().FEATURE_MFA_STATUSES ?? 'ACTIVE, REAL_LOCKED, SELF_EXCLUDED_COOL_OFF, FUNDS_HELD';

    if (this.userSessionService.getLoginResponseForMFA() && mfa_statuses.toUpperCase().includes(this.customer.status.toUpperCase())) {
      this.userSessionService.setMFAStatus(eMfaStatuses.required);
      console.log(this.isSMS_OR_AuthApp)
      if (this.isSMS_OR_AuthApp == 'SMS') {
        this.navigator.navigateTo("SmsLoginComponent", { forceModal: true }, { customerId: this.customer.customerId, callback: this.checkLoginActions })
      } else {
        this.navigator.navigateTo("AuthenticatorAppLoginComponent", { forceModal: true }, { customerId: this.customer.customerId, callback: this.checkLoginActions })
      }
      return;
    } else {
      this.checkLoginActions();
    }
  }
  public checkLoginActions = () => {

    if ('hasNotUpdatedToc' in this.loginInfo && this.loginInfo.hasNotUpdatedToc === true) {
      delete this.loginInfo.hasNotUpdatedToc;
      this.navigator.navigateTo("UpdateTocComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    } else if ('hasNotUpdatedPrivacyPolicy' in this.loginInfo && this.loginInfo.hasNotUpdatedPrivacyPolicy === true) {
      delete this.loginInfo.hasNotUpdatedPrivacyPolicy;
      this.navigator.navigateTo("UpdatePrivacyPolicyComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callback: this.checkLoginActions })
    } else if ('hasNotUpdatedHouseRules' in this.loginInfo && this.loginInfo.hasNotUpdatedHouseRules === true) {
      delete this.loginInfo.hasNotUpdatedHouseRules;
      this.navigator.navigateTo("UpdateHouseRulesComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callback: this.checkLoginActions })
    } else if ('hasExceededDepositLimit' in this.loginInfo) {
      delete this.loginInfo.hasExceededDepositLimit;
      this.navigator.navigateTo("DepositThresholdComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    } else if ('strongAuthenticationRequired' in this.loginInfo) {
      localStorage.setItem('strongAuthenticationRequired', 'true');
      delete this.loginInfo.strongAuthenticationRequired;
      this.navigator.navigateTo("PinLoginComponent", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    } else if ('forcePasswordReset' in this.loginInfo && this.loginInfo?.forcePasswordReset === true) {
      localStorage.setItem('forcePasswordReset', 'true');
      delete this.loginInfo.forcePasswordReset;
      const kycStepValue = this.getKycStepValue(this.customer);

      if (kycStepValue) {
        this.isSMS_OR_AuthApp = kycStepValue == 'SMS' ? 'SMS' : 'AUTH_APP'
      } else {
        this.isSMS_OR_AuthApp = this.config.getConfig().FEATURE_MFA_TYPE ?? 'SMS';
      }
      console.log(this.isSMS_OR_AuthApp);
      if (this.userSessionService.getLoginResponseForMFA()) {
        return this.navigator.navigateTo("ChangePasswordComponent", { forceModal: true }, { playerMigrationCheck: true, callBackFn: this.checkLoginActions });
      } else {
        if (this.isSMS_OR_AuthApp == 'AUTH_APP') return this.navigator.navigateTo("ForcePasswordAuthAppComponent", { forceModal: true }, { customerId: this.customer.customerId, playerMigrationCheck: true, callBackFn: this.checkLoginActions })
        return this.navigator.navigateTo("ForcePasswordSmsComponent", { forceModal: true }, { playerMigrationCheck: true, callBackFn: this.checkLoginActions })
      }
      //this.navigator.navigateTo("ChangePasswordComponent", { forceModal: true }, { playerMigrationCheck: true, callBackFn: this.checkLoginActions });
    } else if ('hasNotW2gEletronicMailConsent' in this.loginInfo) {
      console.log("test show w2g", this.loginInfo);
      delete this.loginInfo.hasNotW2gEletronicMailConsent;
      this.navigator.navigateTo("W2GPopover", { forceModal: true }, { customerId: this.loginInfo.customerId, callBackFn: this.checkLoginActions })
    }
    else {
      this.afterLoginActions();
    }
  }

  showEligibility() {
    if (this.checkForEligibility()) {
      this.isFromBiometrics = false
      setTimeout(() => {
        this.navigator.navigateTo("PlayerEligibilityDialogBoxComponent", { forceModal: true }, { myThis: this, callBackFn: this.onFailCall });
      }, 1000);
    }
  }

  checkForEligibility() {
    const environmentName = this.config.getState()?.toLowerCase();
    return this.isFromBiometrics && (environmentName && environmentName == "ontario")
  }

  onFailCall(mythis) {
    var myThis = mythis
    myThis.eventsService.publish('logout:refresh');
  }

  afterLoginActions = () => {
    this.userSessionService.refreshCustomer().subscribe((customer: CustomerModel) => {
      this.eventsService.publish('event:login', {
        customer_id: customer.customerId,
      });
      //when using tab name - it should match with app.module.ts route name


      // this.eventsService.publish('action:changeTab', 'casino');


      this.eventsService.publish('event:update:banners');
      this.initToastMessages();
      let params = {
        triggeredBy: 'LOGIN'
      }
      this.eventsService.publish('event:appInit:init', params);
    });
  }

  public initGeo = () => {
    this.navigator.dismiss();
    setTimeout(() => {
       if (this.geoLocationStrategyService.isGeoOptimizationEnabled()) {
        this.geoLocationControllerService.executeGeoLocationProcess()
          .then((geoResp: GeoResponse) => {
            if (geoResp.is_allowed) {
              return console.log('geo check passed');
            } else {
              console.log('geo check failed');
            }
          }).catch((err) => {
            console.log("initGeo: launch aborted.", err);
          });
       } else {
         this.jurisdiction
           .verifyJurisdiction(() => this.geoConsole())
           .then((geoResp: GeoResponse) => { })
           .catch((err: any) => { });
       }
    }, 1000);

  }

  public geoConsole() {
    console.log("Geo is Running");
  }

  getKycStepValue(customerResponse): any | false {
    const kycStepData = customerResponse.customData.find(data => data.key === 'MFA_METHOD');
    return kycStepData ? kycStepData.value : false;
  }
}

